import React from 'react';
import Form from 'react-bootstrap/Form';
import { Col, Container, FormGroup, Row, Modal, Alert } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { useState, useEffect, useRef } from 'react';
import { Auth, SignOut, ListIDPs, ListScimGroups } from '../../../functions/ZPA';
import Spinner from 'react-bootstrap/Spinner';
import { library } from '@fortawesome/fontawesome-svg-core'
import { faAngleDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Multiselect from 'multiselect-react-dropdown';
import Badge from 'react-bootstrap/Badge';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { objectToOperationString, url } from '../../../functions/misc';
import InputGroup from 'react-bootstrap/InputGroup';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode'
import { groups_object } from '../../tabs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

library.add(faAngleDown)

function PolicyGroupMigration({ groups, verified, showVerifyWindow, setShowVerifyWindow, setVerifyOperation, setVerified, selectedTab }) {
    const [isLoading, setLoading] = useState(0);
    const [loadingMessage, setLoadingMessage] = useState("Authenticating...")
    const [tenant, setTenant] = useState("Rheintec");
    const [outputConsole, setOutputConsole] = useState("");
    const [accessToken, setAccessToken] = useState("");
    const textareaRef = useRef();
    const [description, setDescription] = useState("")
    const [descriptionTag, setDescriptionTag] = useState("")
    const [idPairs, setIdPairs] = useState([['', '']]);
    const [oldIdHeader, setOldIdHeader] = useState('');
    const [newIdHeader, setNewIdHeader] = useState('');
    const [file, setFile] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [policyRangeStart, setPolicyRangeStart] = useState('');
    const [policyRangeEnd, setPolicyRangeEnd] = useState('');
    const [updatedPolicies, setUpdatedPolicies] = useState([]);
    const [failedPolicies, setFailedPolicies] = useState([]);
    const [notModifiedPolicies, setNotModifiedPolicies] = useState([]);
    const multiselectRef = useRef();
    const fileInputRef = useRef(null);


    function formatPolicyForExcel(policy) {
        return {
            'Nb': policy.progress,
            'ID': policy.id,
            'Name': policy.name,
            'Error': policy.error || ''
        };
    }

    function downloadExcel() {
        const workbook = XLSX.utils.book_new();

        // Updated policies sheet
        if (updatedPolicies.length > 0) {
            const updatedWorksheet = XLSX.utils.json_to_sheet(updatedPolicies.map(formatPolicyForExcel));
            XLSX.utils.book_append_sheet(workbook, updatedWorksheet, "Updated Policies");
        }

        // Failed policies sheet
        if (failedPolicies.length > 0) {
            const failedWorksheet = XLSX.utils.json_to_sheet(failedPolicies.map(formatPolicyForExcel));
            XLSX.utils.book_append_sheet(workbook, failedWorksheet, "Failed Policies");
        }

        // Not modified policies sheet
        if (notModifiedPolicies.length > 0) {
            const notModifiedWorksheet = XLSX.utils.json_to_sheet(notModifiedPolicies.map(formatPolicyForExcel));
            XLSX.utils.book_append_sheet(workbook, notModifiedWorksheet, "Not Modified Policies");
        }

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        saveAs(data, 'policy_migration_results.xlsx');
    }


    const handleFileUpload = (e) => {
        const uploadedFile = e.target.files[0];
        setFile(uploadedFile);
        setShowModal(true); // Show the modal to ask for column headers
    };

    const isPolicyRangeValid = () => {
        if (policyRangeStart === '' && policyRangeEnd === '') {
            return true; // Both empty is valid
        }
        if (policyRangeStart !== '' && policyRangeEnd !== '') {
            return Number(policyRangeEnd) >= Number(policyRangeStart); // End must be >= Start
        }
        return false; // One filled, one empty is invalid
    };

    const getPolicyRangeError = () => {
        if (policyRangeStart !== '' && policyRangeEnd === '') {
            return "Both start and end must be set if one is provided.";
        }
        if (policyRangeStart === '' && policyRangeEnd !== '') {
            return "Both start and end must be set if one is provided.";
        }
        if (policyRangeStart !== '' && policyRangeEnd !== '' && Number(policyRangeEnd) < Number(policyRangeStart)) {
            return "End value must be greater than or equal to Start value.";
        }
        return "";
    };

    const handleModalSubmit = () => {
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const binaryStr = event.target.result;
                const workbook = XLSX.read(binaryStr, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                // Check if the headers exist in the file
                if (!jsonData[0] || !(oldIdHeader in jsonData[0]) || !(newIdHeader in jsonData[0])) {
                    setErrorMessage('The specified column headers were not found in the Excel file.');
                    return;
                }

                // Convert jsonData to 2D array based on the provided headers
                const processedData = jsonData.map((row) => [row[oldIdHeader], row[newIdHeader]]);
                setIdPairs(processedData.length ? processedData : [['', '']]);
                setErrorMessage(''); // Clear any previous error
            };
            reader.readAsBinaryString(file);
        }
        setShowModal(false);
    };

    const handleInputChange = (index, type, value) => {
        const updatedPairs = [...idPairs];
        updatedPairs[index][type === 'old' ? 0 : 1] = value;
        setIdPairs(updatedPairs);

        // Add a new empty row if the last one is completely filled
        if (updatedPairs[updatedPairs.length - 1][0] !== '' && updatedPairs[updatedPairs.length - 1][1] !== '') {
            setIdPairs([...updatedPairs, ['', '']]);
        }
    };

    useEffect(() => {
        const lastPair = idPairs[idPairs.length - 1];
        if (lastPair[0] !== '' && lastPair[1] !== '') {
            setIdPairs([...idPairs, ['', '']]);
        }
    }, [idPairs]);

    const handleRemoveRow = (index) => {
        const updatedPairs = idPairs.filter((_, i) => i !== index);

        // Ensure at least one empty row remains at the end
        if (updatedPairs.length === 0 || updatedPairs[updatedPairs.length - 1][0] !== '' || updatedPairs[updatedPairs.length - 1][1] !== '') {
            updatedPairs.push(['', '']);
        }

        setIdPairs(updatedPairs);
    };

    const handleButtonClick = () => {
        fileInputRef.current.click();
    };

    function copyList(list) {
        const text = ListToText(list);
        navigator.clipboard.writeText(text);
    }

    function ListToText(list) {
        let text = "";
        for (let i = 0; i < list.length; i++) {
            text += i === 0 ? list[i] : "\n" + list[i];
        }
        return text;
    }

    function appendConsole(text, color = 'white') {
        setOutputConsole(prevOutput => prevOutput + `<span style="color: ${color}">${text}</span>`);
    }

    function formatOutput(status, data) {
        const statusWidth = 15;
        const progressWidth = 7;
        const idWidth = 20;
        const nameWidth = 50;
        const paddedStatus = status.trim().padEnd(statusWidth, ' ');
        const paddedProgress = `${data.progress}/${data.total}`.padEnd(progressWidth, ' ');
        const paddedId = data.id.padEnd(idWidth, ' ');
        const paddedName = data.name.slice(0, nameWidth).padEnd(nameWidth, ' ');
        const color = data.updated ? 'lime' : (data.error ? 'red' : 'yellow');

        let output = `\n${paddedProgress} ${paddedStatus} ${paddedId} ${paddedName}`;

        if (data.error) {
            output += `\n${''.padStart(progressWidth + statusWidth + idWidth + nameWidth + 3, ' ')}Error: ${data.error}`;
        }

        return { text: output, color };
    }

    function appendConsoleSeperatorMain(skipNewLine) {
        const width = textareaRef.current.clientWidth;
        const dashesCount = Math.floor(width / 8); // Adjust the number as needed
        const dashes = ('-'.repeat(dashesCount));
        setOutputConsole(prevOutput => prevOutput + (skipNewLine ? "" : "\n") + dashes);
    }

    function appendConsoleSeperatorSecondary() {
        const width = textareaRef.current.clientWidth;
        const dashesCount = Math.floor(width / 8); // Adjust the number as needed
        const dashes = ('-'.repeat(dashesCount / 2));
        setOutputConsole(prevOutput => prevOutput + "\n" + "\t" + dashes);
    }

    async function authenticate(tenant) {
        try {
            const response = await Auth(tenant);
            const auth_json = await response.json();
            if (response.status === 200) {
                const access_token = auth_json.access_token;
                return access_token;
            }
            else {
                appendConsole(`\nAuth Error: ${tenant} - ${auth_json.message}`);
                appendConsoleSeperatorMain();
            }
        }
        catch (error) {
            console.log(error)
            appendConsole(`\nAuth Error: ${tenant} - ${error.message}`);
            appendConsoleSeperatorMain();
        }
    }

    async function handleSubmit(event) {
        setLoading(1);
        setOutputConsole(''); // Clear previous output
        setUpdatedPolicies([]);
        setNotModifiedPolicies([]);
        setFailedPolicies([]);
        event.preventDefault();
        if (!verified) {
            try {
                const obj = {
                    "Tenant": tenant,
                    "Operation": "Migrate Policy Groups",
                    "Affected Resources": {
                        "Access Policies nb": `${policyRangeStart} - ${policyRangeEnd}`,
                        "SCIM Groups IDs (Old,New)": idPairs.map((pair) => pair[0] && pair[1] ? `${pair[0]} - ${pair[1]}` : "").filter((pair) => pair !== ""),
                    }
                }
                const operationString = objectToOperationString(obj);
                setVerifyOperation(operationString);
                setShowVerifyWindow(true);

            } catch (error) {
                console.error(error)
            } finally {
                event.preventDefault();
                return
            }
        }
        let socket;
        let socketClosed = false;

        try {
            setLoadingMessage("Initiating Session...");
            const access_token = await authenticate(tenant);
            setAccessToken(access_token);
            const JWT = Cookies.get('CF_Authorization');

            // Initialize session and get UUID
            const sessionResponse = await fetch(`${url}/api/zpa/policies/session`, {
                method: 'POST',
                headers: {
                    'access_token': access_token,
                    'tenant': tenant,
                    'JWT': JWT
                }
            });
            if (!sessionResponse.ok) {
                throw new Error('Failed to initialize session');
            }
            const sessionData = await sessionResponse.json();
            const uuid = sessionData.uuid;
            appendConsole(`Authenticated`);
            appendConsoleSeperatorMain();
            setLoading(5);

            setLoadingMessage("Initiating WebSocket Connection...");
            const nonEmptyPairs = idPairs.filter(pair => pair[0] !== '' && pair[1] !== '');
            const encodedIdPairs = encodeURIComponent(JSON.stringify(nonEmptyPairs));

            // Use the correct WebSocket URL
            const wsProtocol = url.split(":")[0] == 'https' ? 'wss:' : 'ws:';
            let wsUrl = `${wsProtocol}//${url.split('://')[1]}/api/zpa/policies/migrate?id_pairs=${encodedIdPairs}&uuid=${uuid}&tenant=${tenant}&description=${descriptionTag + "/" + description}`;
            if (isPolicyRangeValid() && policyRangeStart && policyRangeEnd) {
                wsUrl += `&policy_range_start=${policyRangeStart}&policy_range_end=${policyRangeEnd}`;
            } socket = new WebSocket(wsUrl);

            // Create a promise that resolves when the socket is closed
            const socketClosePromise = new Promise((resolve) => {
                socket.onopen = () => {
                    setLoadingMessage("Updating Policies...");
                    console.log('WebSocket connection established');
                };

                socket.onmessage = (event) => {
                    const data = JSON.parse(event.data);
                    console.log('Received update:', data);
                    if (data.message) {
                        appendConsole(`\n${data.message}`);
                    } else {
                        if (data.error) {
                            setFailedPolicies(prev => [...prev, data]);
                        } else if (data.updated) {
                            setUpdatedPolicies(prev => [...prev, data]);
                        } else {
                            setNotModifiedPolicies(prev => [...prev, data]);
                        }
                        const status = data.error ? 'Error' : (data.updated ? 'Updated' : 'Not Modified');
                        const { text, color } = formatOutput(status, data);
                        appendConsole(text, color);
                    }
                    setLoading(data.progress / policyRangeEnd * 100);
                };

                socket.onerror = (error) => {
                    console.error('WebSocket error:', error);
                    appendConsole("\nConnection error occurred");
                    setLoading(0);
                };

                socket.onclose = (event) => {
                    appendConsoleSeperatorMain(false);
                    console.log('WebSocket connection closed:', event);
                    appendConsole("\nConnection closed");
                    setLoading(0);
                    socketClosed = true;
                    resolve();
                };
            });

            // Wait for the socket to close before proceeding
            await socketClosePromise;

        } catch (error) {
            appendConsole("\nInternal Error");
            appendConsole("\n" + error.message);
            console.error(error);
            setLoading(0);
        } finally {
            if (!socketClosed && socket && socket.readyState === WebSocket.OPEN) {
                socket.close();
            }
            if (accessToken) {
                await terminateSession(accessToken);
            }
        }
    }

    function closeWebSocket(socket) {
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.close();
        }
    }

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
    }, [outputConsole]);

    useEffect(() => {
        console.log(idPairs)
    }, [idPairs]);

    useEffect(() => {
        if (selectedTab === "policyGroupMigration" && verified) {
            handleSubmit(new Event("submit"));
        }
        setVerified(false);
    }, [verified, selectedTab]);

    useEffect(() => {
        const JWT = Cookies.get('CF_Authorization');
        const decodedJWT = jwtDecode(JWT)
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const date = `${day}-${month}-${year}`;
        setDescriptionTag(`${date}/${decodedJWT.email}`)
    }, []);

    async function terminateSession(access_token) {
        //Signout
        setLoadingMessage("Terminating Session...");
        await SignOut(access_token);
        setLoading(0);
    }
    return (
        <Container className=' pt-4 px-5 text-white pb-6'>
            <Row>
                <Col>
                    <Form onSubmit={handleSubmit}>
                        <FormGroup controlId="company">
                            <Form.Label>
                                Select tenant
                            </Form.Label>
                            <Form.Select aria-label="Default select example" value={tenant} onChange={(e) => setTenant(e.target.value)} >
                                {
                                    Object.keys(groups).map((key, index) => (
                                        <option key={index} value={key}>{key}</option>
                                    ))

                                }
                            </Form.Select>
                        </FormGroup>

                        {idPairs.map((pair, index) => (
                            <Row className='mt-4 align-items-center' key={index}>
                                <Col className="text-center">
                                    Old ID {index + 1}
                                    <Form.Control
                                        className='text-center'
                                        type='text'

                                        value={pair[0]}
                                        onChange={(e) => handleInputChange(index, 'old', e.target.value)}
                                        placeholder='Old Group ID'
                                    />
                                </Col>
                                <Col className="text-center">
                                    New ID {index + 1}
                                    <Form.Control
                                        className='text-center'
                                        type='text'

                                        value={pair[1]}
                                        onChange={(e) => handleInputChange(index, 'new', e.target.value)}
                                        placeholder='New Group ID'
                                    />
                                </Col>
                                {idPairs.length > 1 && (
                                    <Col xs="auto" className="mt-4">
                                        <Button variant="danger" onClick={() => handleRemoveRow(index)}>
                                            Remove
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        ))}
                        <br />
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileUpload}
                            className='mt-4'
                        />
                        <center>
                            <Button className='text-light w-25' variant="primary" onClick={handleButtonClick}>
                                Upload Excel File
                            </Button>
                            {errorMessage && (
                                <Alert className='mt-2' variant="danger" onClose={() => setErrorMessage('')} dismissible>
                                    {errorMessage}
                                </Alert>
                            )}
                        </center>

                        <Row className='mt-4 align-items-center'>
                            <Form.Label>Policy Range Nb (Leave empty for all policies)</Form.Label>
                            <Col>
                                <Form.Group controlId="policyRangeStart">
                                    <Form.Control
                                        type="number"
                                        value={policyRangeStart}
                                        onChange={(e) => setPolicyRangeStart(e.target.value)}
                                        placeholder="Start (Optional)"
                                        isInvalid={!isPolicyRangeValid()}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {getPolicyRangeError()}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group controlId="policyRangeEnd">
                                    <Form.Control
                                        type="number"
                                        value={policyRangeEnd}
                                        onChange={(e) => setPolicyRangeEnd(e.target.value)}
                                        placeholder="End (Optional)"
                                        isInvalid={!isPolicyRangeValid()}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {getPolicyRangeError()}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Modal show={showModal} onHide={() => setShowModal(false)}>
                            <Modal.Header closeButton>
                                <Modal.Title>Provide Column Header Names</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <Form>
                                    <Form.Group>
                                        <Form.Label>Old ID Header</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter Old ID column header"
                                            value={oldIdHeader}
                                            onChange={(e) => setOldIdHeader(e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label>New ID Header</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter New ID column header"
                                            value={newIdHeader}
                                            onChange={(e) => setNewIdHeader(e.target.value)}
                                        />
                                    </Form.Group>
                                </Form>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setShowModal(false)}>
                                    Cancel
                                </Button>
                                <Button className='text-light' variant="primary" onClick={handleModalSubmit} disabled={!oldIdHeader || !newIdHeader}>
                                    Submit
                                </Button>
                            </Modal.Footer>
                        </Modal>
                        <FormGroup controlId="description" className='mt-3'>
                            <Form.Label>
                                Description (Will be added to the description for each policy)
                            </Form.Label>

                            <InputGroup className="">
                                <InputGroup.Text id="basic-addon2">{descriptionTag}</InputGroup.Text>
                                <Form.Control type='text' required value={description} onChange={(e) => setDescription(e.target.value)} placeholder='Some Description...' />
                            </InputGroup>
                        </FormGroup>


                        <Button className='mt-4 text-light' type='submit' disabled={isLoading || idPairs[0][0] == "" || idPairs[0][1] == "" || !isPolicyRangeValid()} variant="success" >
                            {isLoading ?
                                <span>
                                    <Spinner
                                        variant='light'
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    {"  " + loadingMessage}
                                </span> : `Update Policies`}
                        </Button>
                    </Form>
                </Col>
            </Row>
            <Row className='mt-5 '>
                <Col>
                    <ProgressBar className='mb-2' now={isLoading} />
                </Col>
                <Col md="auto" className='text-end'>
                    <Button
                        disabled={updatedPolicies.length === 0 && failedPolicies.length === 0 && notModifiedPolicies.length === 0}
                        onClick={downloadExcel}
                        className='text-light rounded-0 pt-0 pb-0 pm-1'
                        size='sm'
                        variant='primary'
                    >
                        <FontAwesomeIcon className='me-1' size='lg' icon="fa-solid fa-download" style={{ color: "#ffffff", }} />
                        Download Results
                        <Badge className='ms-1' bg="success">{updatedPolicies.length}</Badge>
                        <Badge className='ms-1' bg="danger">{failedPolicies.length}</Badge>
                        <Badge className='ms-1' bg="warning">{notModifiedPolicies.length}</Badge>
                    </Button>
                </Col>
            </Row>
            <Row className='mt-0 text-center'>
                <div
                    className='w-100 bg-dark-subtle text-rheintec-blue output-terminal'
                    style={{ height: '400px', overflowY: 'scroll', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                    dangerouslySetInnerHTML={{ __html: outputConsole }}
                    ref={textareaRef}
                >
                </div>


            </Row>

        </Container >

    );

}

export default PolicyGroupMigration;